// BANK1 (docs/build-briefs/BANK1-bank-accounts-in-app.md; DEC-509). Settings > Tabs and payment > Bank accounts: one card with
// three rows (Deposits, Royalties, Snip & Sip subscription), each with a status, "Account ending 1234" when Stripe gave one, and
// one action (Connect, Change or Fix). Every bank is connected on Stripe's own pages (hosted onboarding for Deposits, hosted
// bank sign-in through Financial Connections for the two debit authorizations): no routing or account number is typed, held or
// shown here, and no account, customer or payment method id is ever here.
//
// The location is the tablet's. A hosted address is returned once by the server and handed straight to the browser, only when
// it is https on a Stripe host (or the simulated host in test mode): never kept, shown or logged. For the two debit
// authorizations the person sees the authorization text first and confirms (Continue on Stripe), so nothing is authorized by
// one stray tap. Dark while connected accounts are off (the reads answer 404 and the card stays hidden). Admin surface:
// pointer and keyboard are fine, and every control is 44px or taller. All text via textContent.

import { showBillingMode } from "./billingModeWords.ts";
import {
  getCardPayments,
  getRoyaltyAuthorization,
  getSubscriptionDebitAuthorization,
  postCardPaymentsOnboardingLink,
  postCardPaymentsSetUp,
  postRoyaltyAuthorizationReturn,
  postRoyaltyAuthorizationStart,
  postSubscriptionDebitAuthorizationReturn,
  postSubscriptionDebitAuthorizationStart,
  type RoyaltyAuthorizationDto,
  type SubscriptionDebitDto,
} from "./api.ts";
import {
  bankAccountsView,
  payoutSettingsUrl,
  type BankRowId,
  type BankRowView,
} from "./bankAccountsModel.ts";
import {
  onboardingNavigationTarget,
  type CardPaymentsForLocationDto,
} from "./cardPaymentsModel.ts";

/** Which Stripe page the browser is being sent to, so the caller can leave the right return marker behind. */
export type BankNavigation = "deposits" | "royalties" | "subscription";

export interface BankAccountsSectionDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.terminal.view (the Settings read of this card). */
  canView: () => boolean;
  /** The session holds admin.payments.onboard. */
  canOnboard: () => boolean;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
  /** Takes the browser to Stripe's page. */
  navigate: (which: BankNavigation, url: string) => void;
  /** Opens a Stripe page in a new tab (the owner's own Dashboard). */
  openExternal: (url: string) => void;
}

export type BankArrivalSource = "royalties" | "subscription";

export interface BankAccountsSection {
  refresh(): Promise<void>;
  /** The browser came back from a hosted bank debit page. */
  handleArrival(source: BankArrivalSource, arrival: "return" | "cancel"): Promise<void>;
  reset(): void;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

function messageOf(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "Something went wrong. Try again.";
}

const CHIP_CLASS = { good: "fl-chip-good", warn: "fl-chip-warn", neutral: "fl-chip-neutral" } as const;

export function mountBankAccountsSection(
  root: HTMLElement,
  deps: BankAccountsSectionDeps,
): BankAccountsSection {
  let card: CardPaymentsForLocationDto | undefined;
  let royalty: RoyaltyAuthorizationDto | undefined;
  let subscription: SubscriptionDebitDto | undefined;
  let statusLine = "";
  let errorLine = "";
  let errorRow: BankRowId | undefined;
  let confirming: BankRowId | undefined;
  let loadSequence = 0;
  let opening = false;
  root.hidden = true;

  function reset(): void {
    loadSequence += 1;
    card = undefined;
    royalty = undefined;
    subscription = undefined;
    statusLine = "";
    errorLine = "";
    errorRow = undefined;
    confirming = undefined;
    opening = false;
    root.replaceChildren();
    root.hidden = true;
  }

  function mode(): "test" | "live" | undefined {
    return card?.mode ?? royalty?.mode ?? subscription?.mode;
  }

  async function read(): Promise<void> {
    if (!deps.canView()) {
      reset();
      return;
    }
    const sequence = (loadSequence += 1);
    const [cardResult, royaltyResult, subscriptionResult] = await Promise.all([
      getCardPayments(deps.apiBaseUrl(), deps.tenantId()),
      getRoyaltyAuthorization(deps.apiBaseUrl(), deps.tenantId()),
      getSubscriptionDebitAuthorization(deps.apiBaseUrl(), deps.tenantId()),
    ]);
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    // Off for this deployment, not allowed, or a device with no location: that row shows nothing and says nothing.
    card =
      cardResult.status === 200 && Array.isArray(cardResult.body.otherLocations)
        ? cardResult.body
        : undefined;
    royalty =
      royaltyResult.status === 200 && royaltyResult.body.applies === true
        ? royaltyResult.body
        : undefined;
    subscription =
      subscriptionResult.status === 200 && subscriptionResult.body.applies === true
        ? subscriptionResult.body
        : undefined;
    // A hosted page was started and the tab was closed before it returned: Stripe is asked what happened, so the row
    // reads true now rather than at the next return.
    if (subscription?.status === "pending" && deps.canOnboard()) {
      const settled = await postSubscriptionDebitAuthorizationReturn(
        deps.apiBaseUrl(),
        deps.tenantId(),
      );
      if (sequence !== loadSequence || !deps.isCurrent()) return;
      if (settled.status === 200 && settled.body.applies === true) subscription = settled.body;
    }
    render();
  }

  function render(): void {
    root.replaceChildren();
    const view = bankAccountsView({
      card,
      royalty,
      subscription,
      canOnboard: deps.canOnboard(),
    });
    if (!view.offered) {
      root.hidden = true;
      return;
    }
    root.hidden = false;
    const wrap = node("div", "cp-section");
    wrap.append(node("h2", "section-title", "Bank accounts"));
    if (statusLine !== "") {
      const status = node("p", "cp-status-line", statusLine);
      status.setAttribute("role", "status");
      status.dataset["testid"] = "admin-bank-accounts-status";
      wrap.append(status);
    }
    // OWNLIVE1 (P0.31): the subscription debit is a billing screen, so it says which Stripe mode it is in.
    if (subscription?.mode !== undefined) {
      const modeLine = node("p", "so-note", "");
      modeLine.dataset["testid"] = "admin-bank-accounts-mode";
      showBillingMode(modeLine, subscription.mode);
      wrap.append(modeLine);
    }
    const list = node("ul", "ba-rows");
    list.dataset["testid"] = "admin-bank-accounts";
    for (const row of view.rows) list.append(renderRow(row));
    wrap.append(list);
    root.append(wrap);
  }

  function renderRow(row: BankRowView): HTMLElement {
    const item = node("li", `ba-row ba-row-${row.chip.tone}`);
    item.dataset["testid"] = "admin-bank-row";
    item.dataset["row"] = row.id;
    item.dataset["status"] = row.status;
    const main = node("div", "ba-row-main");
    main.append(node("div", "ba-row-title", row.title), node("div", "ba-row-caption", row.caption));
    if (row.detail !== null) {
      const detail = node("div", "ba-row-detail", row.detail);
      detail.dataset["testid"] = "admin-bank-row-detail";
      main.append(detail);
    }
    const side = node("div", "ba-row-side");
    const chip = node("span", `fl-chip ${CHIP_CLASS[row.chip.tone]} ba-chip`);
    chip.dataset["testid"] = "admin-bank-row-status";
    if (row.chip.symbol !== "") chip.append(node("span", "fl-chip-symbol", row.chip.symbol));
    chip.append(document.createTextNode(row.chip.label));
    side.append(chip);
    if (row.viewOnly) {
      const viewOnly = node("span", "cp-tag", "View only");
      viewOnly.dataset["testid"] = "admin-bank-row-view-only";
      side.append(viewOnly);
    }
    if (row.action !== null) {
      const button = node("button", "btn-secondary btn-compact", row.action.label);
      button.type = "button";
      button.dataset["testid"] = "admin-bank-row-action";
      button.disabled = opening;
      button.setAttribute("aria-expanded", String(confirming === row.id));
      button.addEventListener("click", () => onAction(row, button));
      side.append(button);
    }
    const head = node("div", "ba-row-head");
    head.append(main, side);
    item.append(head);
    if (confirming === row.id && row.disclosure !== null) item.append(renderConfirm(row));
    if (errorLine !== "" && errorRow === row.id) {
      const error = node("p", "admin-error cp-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = "admin-bank-row-error";
      item.append(error);
    }
    return item;
  }

  /** The authorization text, then Continue on Stripe or Cancel. Shown before any redirect for a debit authorization. */
  function renderConfirm(row: BankRowView): HTMLElement {
    const panel = node("div", "ba-confirm");
    panel.dataset["testid"] = "admin-bank-row-confirm";
    const text = node("p", "cp-fact", row.disclosure ?? "");
    text.dataset["testid"] = "admin-bank-row-disclosure";
    const actions = node("div", "ba-confirm-actions");
    const cancel = node("button", "btn-secondary btn-compact", "Cancel");
    cancel.type = "button";
    cancel.dataset["testid"] = "admin-bank-row-cancel";
    cancel.addEventListener("click", () => {
      confirming = undefined;
      render();
    });
    const go = node("button", "btn-primary btn-compact", "Continue on Stripe");
    go.type = "button";
    go.dataset["testid"] = "admin-bank-row-continue";
    go.disabled = opening;
    go.addEventListener("click", () => void openDebitPage(row.id as "royalties" | "subscription"));
    actions.append(cancel, go);
    panel.append(text, actions);
    return panel;
  }

  function onAction(row: BankRowView, button: HTMLButtonElement): void {
    errorLine = "";
    errorRow = undefined;
    if (row.id === "deposits") {
      if (row.action?.kind === "change") {
        deps.openExternal(payoutSettingsUrl(mode()));
        return;
      }
      void openOnboarding(button, row.action?.kind === "setup");
      return;
    }
    confirming = confirming === row.id ? undefined : row.id;
    render();
  }

  function fail(row: BankRowId, text: string): void {
    opening = false;
    errorRow = row;
    errorLine = text;
    render();
  }

  /** Deposits: the existing Account Link (or, with no account yet, the set-up that creates it first), minted on the tap and followed once. */
  async function openOnboarding(button: HTMLButtonElement, setUp: boolean): Promise<void> {
    if (opening) return;
    opening = true;
    button.disabled = true;
    const result = setUp
      ? await postCardPaymentsSetUp(deps.apiBaseUrl(), deps.tenantId())
      : await postCardPaymentsOnboardingLink(deps.apiBaseUrl(), deps.tenantId());
    if (!deps.isCurrent()) return;
    if (result.status === 200 && typeof result.body.url === "string") {
      const target = onboardingNavigationTarget(result.body.url, mode());
      if (target !== null) {
        deps.navigate("deposits", target);
        return;
      }
      fail("deposits", "Link not valid");
      return;
    }
    fail("deposits", messageOf(result.body));
  }

  /** Royalties and Snip & Sip subscription: the hosted bank sign-in page, after the person has seen the authorization text. */
  async function openDebitPage(which: "royalties" | "subscription"): Promise<void> {
    if (opening) return;
    opening = true;
    const result =
      which === "royalties"
        ? await postRoyaltyAuthorizationStart(deps.apiBaseUrl(), deps.tenantId())
        : await postSubscriptionDebitAuthorizationStart(deps.apiBaseUrl(), deps.tenantId());
    if (!deps.isCurrent()) return;
    if (result.status === 200 && typeof result.body.url === "string") {
      const target = onboardingNavigationTarget(result.body.url, mode());
      if (target !== null) {
        deps.navigate(which, target);
        return;
      }
      fail(which, "Link not valid");
      return;
    }
    opening = false;
    if (result.status === 200) {
      // The old page had finished meanwhile: show what is true now.
      confirming = undefined;
      await read();
      return;
    }
    fail(which, messageOf(result.body));
  }

  async function handleArrival(source: BankArrivalSource, arrival: "return" | "cancel"): Promise<void> {
    if (!deps.canView()) {
      reset();
      return;
    }
    let kept = "";
    if (arrival === "return" && deps.canOnboard()) {
      const sequence = (loadSequence += 1);
      const result =
        source === "royalties"
          ? await postRoyaltyAuthorizationReturn(deps.apiBaseUrl(), deps.tenantId())
          : await postSubscriptionDebitAuthorizationReturn(deps.apiBaseUrl(), deps.tenantId());
      if (sequence !== loadSequence || !deps.isCurrent()) return;
      if (!(result.status === 200 && result.body.applies === true)) kept = messageOf(result.body);
    }
    await read();
    statusLine = "Back from Stripe";
    if (kept !== "") {
      errorRow = source;
      errorLine = kept;
    }
    render();
  }

  return {
    async refresh() {
      statusLine = "";
      await read();
    },
    handleArrival,
    reset,
  };
}
